import { mpEventInfo } from '@/constant'
import type { DefaultResponse, Pagination } from '@/types'
import type { StudentWorkbookProgressResponse } from '@/types/common/api'
import maxios from '@/utils/axios/maxios'

/***
 * @see https://documenter.getpostman.com/view/5542627/TVzUDwLW#738a1a93-3ccc-4567-81e0-52a938e80ea7
 ***/

//학생-교재 API
const studentWorkbookApi = {
  /* 학생*/
  // 학생 교재 목록 불러오기
  getStudentWorkbookList: (
    { studentId, ...params }: req.StudentWorkbook,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.StudentWorkbook[]>>(
      `/student-workbook/student/${studentId}${pageQueryString}`,
      {
        params: { ...params, mp_event_name: mpEventInfo.특정학생출제된교재조회 },
      },
    )
  },
  // 학생 교재 진도 불러오기
  // 학생 교재에 해당하는 진도(페이지) 목록
  getStudentWorkbookProgress: (
    { studentId, studentWorkbookId, revisionId }: req.StudentWorkbookProgress,
    pageQueryString: string,
  ) => {
    return maxios.get<StudentWorkbookProgressResponse<res.StudentWorkbookProgress[]>>(
      `/student-workbook/student/${studentId}/${studentWorkbookId}/${revisionId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.특정학생출제된교재진도조회 },
      },
    )
  },
  //학생교재의 특정 페이지 채점 기록 불러오기
  // 학생 진도에 해당하는 문제 목록
  getStudentWorkbookPageDetail: <T extends res.StudentWorkbookPageDetail>(
    { studentId, studentWorkbookId, revisionId, progressId }: req.StudentWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<T[]>>(
      `/student-workbook/student/${studentId}/${studentWorkbookId}/${revisionId}/${progressId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.특정학생출제된교재채점조회 },
      },
    )
  },

  /* 학년*/
  // 학교급, 학년에 해당하는 학생의 교재 목록 불러오기
  // 학년 교재 목록 불러오기
  getGradeWorkbookList: (
    { schoolType, grade, ...params }: req.GradeWorkbook,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.GroupWorkbook[]>>(
      `/student-workbook/grade/${schoolType}/${grade}${pageQueryString}`,
      { params: { ...params, mp_event_name: mpEventInfo.학년별출제된교재조회 } },
    )
  },
  // 학교급, 학년에 해당하는 교재의 진도 불러오기
  // 학년 교재에 해당하는 진도(페이지) 목록
  getGradeWorkbookProgress: (
    { schoolType, grade, workbookId, round }: req.GradeWorkbookProgress,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.GroupWorkbookProgress[]>>(
      `/student-workbook/grade/${schoolType}/${grade}/${workbookId}/${round}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.학년별출제된교재진도조회 },
      },
    )
  },
  // 학교급, 학년에 해당하는 교재의 채점기록 풀러오기
  // 학년 진도에 해당하는 문제 목록
  getGradeWorkbookPageDetail: <T extends res.GroupWorkbookPageDetail>(
    { schoolType, grade, workbookId, pageId, round }: req.GradeWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<T[]>>(
      `/student-workbook/grade/${schoolType}/${grade}/${workbookId}/${round}/${pageId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.학년별출제된교재채점조회 },
      },
    )
  },

  /* 반*/
  // 반 학생이 가진 교재 목록 불러오기
  getClassWorkbookList: (
    { lessonClassId, ...params }: req.ClassWorkbook,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.GroupWorkbook[]>>(
      `/student-workbook/lesson-classes/${lessonClassId}${pageQueryString}`,
      {
        params: { ...params, mp_event_name: mpEventInfo.반별출제된교재조회 },
      },
    )
  },
  // 그룹 교재 진도 정보
  getClassWorkbookProgress: (
    { lessonClassId, workbookId, round }: req.ClassWorkbookProgress,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<res.GroupWorkbookProgress[]>>(
      `/student-workbook/lesson-classes/${lessonClassId}/${workbookId}/${round}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.반별출제된교재진도조회 },
      },
    )
  },
  // 해당 학생들의 페이지 채점 기록을 불러온다
  getClassWorkbookPageDetail: <T extends res.GroupWorkbookPageDetail>(
    { lessonClassId, workbookId, pageId, round }: req.ClassWorkbookPageDetail,
    pageQueryString: string,
  ) => {
    return maxios.get<Pagination<T[]>>(
      `/student-workbook/lesson-classes/${lessonClassId}/${workbookId}/${round}/${pageId}${pageQueryString}`,
      {
        params: { mp_event_name: mpEventInfo.반별출제된교재채점조회 },
      },
    )
  },

  // 학생에게 교재 부여하기 - 이미 부여되어 있으면 회차 추가
  postAddWorkbookToStudent: (data: req.AddWorkbookToStudent) => {
    return maxios.post(`/student-workbook`, data, {
      params: { mp_event_name: mpEventInfo.교재출제재출제 },
    })
  },
  // 학생에게 부여된 교재 삭제하기
  deleteStudentWorkbook: (workbookIds: req.DeleteStudentWorkbook) => {
    return maxios({
      url: '/student-workbook',
      method: 'DELETE',
      data: workbookIds,
      params: { mp_event_name: mpEventInfo.교재출제취소 },
    })
  },
  // 학생에게 부여된 회자 삭제하
  deleteStudentWorkbookRevision: (revisionIds: req.StudentWorkbookProgress['revisionId'][]) => {
    return maxios({
      url: '/student-workbook/version',
      method: 'DELETE',
      data: revisionIds,
      params: { mp_event_name: mpEventInfo.특정교재_회차출제취소 },
    })
  },

  // 학생에게 부여된 교재의 진도 변경하기
  postStudentWorkbookProgressStatus: (data: req.StudentWorkbookProgressChange) => {
    return maxios.post<DefaultResponse<req.WorkbookStatus>>(`/student-workbook/progress`, data, {
      params: { mp_event_name: mpEventInfo.특정교재_진도상태수정 },
    })
  },

  // 학생에게 부여된 교재 문제 채점하기
  patchWorkbookResult: (data: req.StudentWorkbookScoring[]) => {
    return maxios.patch(`/student-workbook/scoring`, data, {
      params: { mp_event_name: mpEventInfo.교재문제채점, version: 'v2' },
    })
  },
}

export default studentWorkbookApi
